// src/Components/Forms/Financement/Salam/Validation/ReadOnlyDocumentList.tsx
import React, { useState, useMemo } from 'react';
import {
  Row, Col, Card, Modal, Button, Tag, Typography, Empty, Spin, Divider, Space,
} from 'antd';
import {
  EyeOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  UserOutlined,
  CalendarOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { UploadedDocument } from './SetMultipleDocument'; // adaptez le chemin

interface ReadOnlyDocumentListProps {
  documents: UploadedDocument[];
  loading?: boolean;
}

const getFileIcon = (ext: string | null) => {
  if (ext === 'pdf') return <FilePdfOutlined style={{ fontSize: 20, color: '#d4380d' }} />;
  return <FileImageOutlined style={{ fontSize: 20, color: '#096dd9' }} />;
};

const formatKo = (ko: number) => (ko >= 1024 ? `${(ko / 1024).toFixed(1)} Mo` : `${ko} Ko`);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const getStatusTag = (statut: string | null) => {
  if (!statut) return null;
  let color = 'default';
  if (statut === 'VALIDE') color = 'green';
  else if (statut === 'REJETE') color = 'red';
  else if (statut === 'EXPIRE') color = 'orange';
  else if (statut === 'CHARGE') color = 'blue';
  return <Tag color={color}>{statut}</Tag>;
};

const ReadOnlyDocumentList: React.FC<ReadOnlyDocumentListProps> = ({ documents, loading }) => {
  const [previewDoc, setPreviewDoc] = useState<UploadedDocument | null>(null);

  // Regrouper les documents par étape
  const documentsByEtape = useMemo(() => {
    if (!documents || documents.length === 0) return {};
    
    const grouped: Record<string, UploadedDocument[]> = {};
    documents.forEach(doc => {
      const etape = doc.CodeEtape || 'SANS_ETAPE';
      if (!grouped[etape]) {
        grouped[etape] = [];
      }
      grouped[etape].push(doc);
    });
    
    return grouped;
  }, [documents]);

  if (loading) {
    return <Spin spinning size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} />;
  }

  if (!documents || documents.length === 0) {
    return <Empty description="Aucun document attaché" />;
  }

  const getContentType = (doc: UploadedDocument) => {
    const ext = doc.ExtensionFichier;
    if (ext === 'pdf') return 'application/pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext || '')) return `image/${ext}`;
    return '';
  };

  return (
    <>
      <div style={{ background: '#fafafa', borderRadius: 8, padding: 12 }}>
        {Object.entries(documentsByEtape).map(([etape, docs], index) => (
          <div key={etape} style={{ marginBottom: index < Object.keys(documentsByEtape).length - 1 ? 16 : 0 }}>
            {/* En-tête de l'étape */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 10,
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 6,
              boxShadow: '0 1px 4px rgba(102, 126, 234, 0.2)'
            }}>
              <FolderOutlined style={{ fontSize: 16, color: '#fff' }} />
              <Typography.Text strong style={{ margin: 0, color: '#fff', flex: 1, fontSize: 13 }}>
                Étape: {etape}
              </Typography.Text>
              <Tag color="white" style={{ color: '#667eea', fontWeight: 600, fontSize: 11, padding: '0 6px', margin: 0 }}>
                {docs.length}
              </Tag>
            </div>

            {/* Documents de l'étape */}
            <Row gutter={[12, 12]}>
              {docs.map((doc) => {
                const isLoaded = !!doc.FichierBinaire;
                return (
                  <Col xs={24} sm={12} lg={8} key={doc.TypeCodeDoc}>
                    <Card
                      size="small"
                      style={{
                        borderRadius: 6,
                        borderLeft: `3px solid ${isLoaded ? '#52c41a' : '#d9d9d9'}`,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        height: '100%',
                      }}
                      bodyStyle={{ padding: 10 }}
                      hoverable
                    >
                      {/* Titre et statut */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <Typography.Text strong style={{ fontSize: 12, lineHeight: '16px', flex: 1, marginRight: 6 }}>
                          {doc.LibelleDoc || doc.TypeCodeDoc}
                        </Typography.Text>
                        {isLoaded ? (
                          <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                        ) : (
                          <ExclamationCircleFilled style={{ color: '#ff7875', fontSize: 16 }} />
                        )}
                      </div>

                      {/* Tags de statut et taille */}
                      <div style={{ marginBottom: 6 }}>
                        {getStatusTag(doc.StatutDoc)}
                        {doc.TailleFichier && (
                          <Tag color="default" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>
                            {formatKo(doc.TailleFichier)}
                          </Tag>
                        )}
                      </div>

                      {/* Informations de traçabilité */}
                      <div style={{
                        background: '#f5f5f5',
                        borderRadius: 4,
                        padding: '6px 8px',
                        marginBottom: 6,
                        fontSize: 10
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                          <UserOutlined style={{ color: '#1890ff', fontSize: 10 }} />
                          <Typography.Text style={{ fontSize: 10, color: '#595959' }}>
                            {doc.CreePar || 'N/A'}
                          </Typography.Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <CalendarOutlined style={{ color: '#52c41a', fontSize: 10 }} />
                          <Typography.Text style={{ fontSize: 10, color: '#595959' }}>
                            {formatDate(doc.DateCreation)}
                          </Typography.Text>
                        </div>
                      </div>

                      {/* Zone fichier */}
                      {isLoaded ? (
                        <div
                          onClick={() => setPreviewDoc(doc)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 8px',
                            background: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: 4,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#e6f7e6';
                            e.currentTarget.style.borderColor = '#95de64';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f6ffed';
                            e.currentTarget.style.borderColor = '#b7eb8f';
                          }}
                        >
                          {getFileIcon(doc.ExtensionFichier)}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {doc.NomFichier}
                            </div>
                          </div>
                          <Button
                            type="primary"
                            size="small"
                            shape="circle"
                            icon={<EyeOutlined />}
                            style={{ width: 24, height: 24, minWidth: 24 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewDoc(doc);
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            padding: '6px 8px',
                            background: '#fff1f0',
                            border: '1px solid #ffa39e',
                            borderRadius: 4,
                            color: '#cf1322',
                            textAlign: 'center',
                            fontSize: 11,
                            fontWeight: 500
                          }}
                        >
                          Non chargé
                        </div>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
            
            {index < Object.keys(documentsByEtape).length - 1 && (
              <Divider style={{ margin: '12px 0' }} />
            )}
          </div>
        ))}
      </div>

      {/* Modal d'aperçu */}
      <Modal
        open={!!previewDoc}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getFileIcon(previewDoc?.ExtensionFichier ?? null)}
            <span>{previewDoc?.LibelleDoc}</span>
          </div>
        }
        onCancel={() => setPreviewDoc(null)}
        footer={[<Button key="close" onClick={() => setPreviewDoc(null)}>Fermer</Button>]}
        width={820}
        centered
        bodyStyle={{ padding: 0, background: '#f5f5f5', borderRadius: '0 0 8px 8px' }}
      >
        <div
          style={{
            minHeight: 500,
            maxHeight: '75vh',
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          {previewDoc?.FichierBinaire &&
            (() => {
              const contentType = getContentType(previewDoc);
              if (contentType.startsWith('image/')) {
                return (
                  <img
                    src={`data:${contentType};base64,${previewDoc.FichierBinaire}`}
                    alt={previewDoc.LibelleDoc}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      borderRadius: 6,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                  />
                );
              }
              if (contentType === 'application/pdf') {
                return (
                  <iframe
                    src={`data:application/pdf;base64,${previewDoc.FichierBinaire}`}
                    title={previewDoc.LibelleDoc}
                    style={{
                      width: '100%',
                      height: '70vh',
                      border: 'none',
                      borderRadius: 6,
                      background: '#fff',
                    }}
                  />
                );
              }
              return <Empty description={`Aperçu non disponible pour ${previewDoc.NomFichier}`} />;
            })()}
        </div>
      </Modal>
    </>
  );
};

export default ReadOnlyDocumentList;